import { useState, useEffect, useCallback } from 'react';
import { productService } from '../services/product.service';

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const DEFAULT_STATS = {
  total: 0,
  active: 0,
  inactive: 0,
  lowStock: 0,
  outOfStock: 0,
};

export const useProducts = (initialFilters = {}, initialPagination = {}, apiMethod = 'getAll') => {
  const [products, setProducts]     = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [stats, setStats]           = useState(DEFAULT_STATS);
  const [filters, setFilters]       = useState(initialFilters);
  const [sortOptions, setSortOptions] = useState({
    sort: initialPagination.sort || 'created_at',
    order: initialPagination.order || 'desc',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchProducts = async (newFilters = filters, newPagination = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: newPagination.page || pagination.page,
        limit: newPagination.limit || pagination.limit,
        ...sortOptions,
        ...newFilters,
      };

      // Calls the specified endpoint (public getAll or seller getMyProducts)
      const response = await productService[apiMethod](params);

      setProducts(response.data?.products ?? response.products ?? []);
      setPagination(response.data?.pagination ?? response.pagination ?? DEFAULT_PAGINATION);
      setStats(response.data?.stats ?? response.stats ?? DEFAULT_STATS);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch products');
      setProducts([]);
      setStats(DEFAULT_STATS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filters, pagination.page, sortOptions]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const updateSort = useCallback((sort, order = 'desc') => {
    setSortOptions({ sort, order });
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const goToPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const nextPage = () => {
    if (pagination.hasNextPage) goToPage(pagination.page + 1);
  };

  const previousPage = () => {
    if (pagination.hasPreviousPage) goToPage(pagination.page - 1);
  };

  const refresh = () => {
    fetchProducts();
  };

  return {
    products,
    pagination,
    stats,        // server-computed — accurate across all pages
    filters,
    sortOptions,
    loading,
    error,
    updateFilters,
    updateSort,
    goToPage,
    nextPage,
    previousPage,
    refresh,
  };
};

export default useProducts;